import { Wallet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, cn } from "@wa/ui";
import { fmtMoney } from "../../../lib/format";
import { type AccountCredit } from "../../../lib/credit";

type T = (k: string, v?: Record<string, string | number>) => string;

/**
 * The account-credit card - balance, expiry and the movement ledger.
 *
 * Credit is earned from the referral programme or the signup bonus and is spendable on TWO
 * paths: one-off purchases in the builder (templates, widgets, header/footer designs) or
 * covering the plan/add-on invoice. Renders whenever the wallet module is on - including at
 * a zero balance, so the account hub's credit shortcut always leads somewhere (the module
 * being off returns 404 -> `credit` stays null -> nothing here).
 *
 * It has its own section and nav item rather than sitting under "current plan": three
 * inbound links (the credit e-mail, the hub's credit chip and the overview's ledger button)
 * aim straight at the ledger, and they used to land behind the entitlement wall. The
 * summary is fetched by the shell rather than here, because the shell has to know whether
 * the wallet exists before it can decide to offer the nav item at all - otherwise the tab
 * shows a "Kontokrediit" link that scrolls to nothing.
 */
export function CreditWalletCard({ credit, locale, t }: { credit: AccountCredit; locale: string; t: T }) {
  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(locale === "et" ? "et-EE" : "en-US");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wallet className="size-5 text-primary" />
          {t("credit.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("credit.available")}</div>
            <div className="text-3xl font-bold tabular-nums">{fmtMoney(credit.availableCents, "EUR", locale)}</div>
          </div>
          {credit.pendingCents > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("credit.pending")}</div>
              <div className="text-lg font-medium tabular-nums text-muted-foreground">
                {fmtMoney(credit.pendingCents, "EUR", locale)}
              </div>
            </div>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          {credit.availableCents > 0 || credit.pendingCents > 0 ? t("credit.spendHint") : t("credit.zeroHint")}
        </p>
        {credit.nextExpiryAt && (
          <p className="text-xs text-muted-foreground">{t("credit.expiresAt", { date: fmtDay(credit.nextExpiryAt) })}</p>
        )}

        {credit.rows.length > 0 && (
          <div>
            <div className="mb-2 text-sm font-medium">{t("credit.ledger")}</div>
            <ul className="divide-y rounded-lg border">
              {credit.rows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate">{t(`credit.kind.${r.kind}`)}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDay(r.createdAt)} · {t(`credit.status.${r.status}`)}
                    </div>
                  </div>
                  <span className={cn("shrink-0 tabular-nums font-medium", r.amountCents < 0 ? "text-muted-foreground" : "text-success")}>
                    {r.amountCents < 0 ? "-" : "+"}
                    {fmtMoney(Math.abs(r.amountCents), "EUR", locale)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
